import {
    Box, Button,
    Checkbox,
    FormControl,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper, TextField,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../components/table/table-pagination.component";
import TableActionComponent from "../../components/table/table-action.component";
import * as React from "react";
import {ErrorComponent} from "../../components/error/error.component";
import {useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import Select from "@mui/material/Select";
import {translate} from "../../translate/translate";
import IconButton from "@mui/material/IconButton";
import {Troubleshoot} from "@mui/icons-material";
import ReplenishmentStockComponent from "./replenishment-stock.component";
import {Replenishment, ReplenishmentSpareParts} from "../../models/basic/replenishment";
import {ReplenishmentService} from "../../services/replenishment-service";

type replenishmentListProps = {
    disabled?: boolean;
    inExecution: (exec: boolean) => void;
    processing: boolean;
    configuration: Replenishment | null;
    hasPermission: boolean;
}

const ReplenishmentListComponent: React.FC<replenishmentListProps> = ({
    disabled,
    inExecution,
    processing,
    configuration,
    hasPermission
}) => {
    const service = new ReplenishmentService();
    const params = useParams();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [dataSource, setDataSource] = React.useState<ReplenishmentSpareParts[]>();
    const [auxDataSource, setAuxDataSource] = React.useState<ReplenishmentSpareParts[]>();
    // Selecionados da lista
    const [selected, setSelected] = useState<any[]>([]);
    // Selecionados do filtro
    const [selectedSearch, setSelectedSearch] = useState<string[]>([]);
    const [filterList, setFilterList] = useState<string[]>([]);

    const [openStock, setOpenStock] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<ReplenishmentSpareParts | null>(null);
    const [initialize, setInitialize] = useState<boolean>(true);

    const [search, setSearch] = useState<string>("");

    useEffect(() => {
        load(initialize);
    }, [selectedSearch]);

    useEffect(() => {
        if (!processing) {
            setSelected([])
        }
    }, [processing]);

    const load = (initialize = true) => {
        const filters = auxDataSource?.filter(it => selectedSearch.includes(it.id.toString()))
        let materials = filters ? filters.map(it => it.material_number) : undefined
        if(auxDataSource?.length === materials?.length){
            materials = undefined;
        }

        setLoading(true);
        service.replenishment_spare_parts(materials).then((response) => {
            setDataSource(response.data);
            if (initialize) {
                setAuxDataSource(response.data);
                const uniqueMaterials = response.data.map((item: any) => item.id.toString())
                setFilterList(uniqueMaterials);
                setSelectedSearch(uniqueMaterials);
            }

        }).catch((error: any) => {
            ErrorComponent(error);
        })
        .finally(() => {
            setLoading(false);
            setInitialize(false);
        });
    }

    const changeSelected = (row: ReplenishmentSpareParts) => {
        const isSelected = selected.some((item) => item.id === row.id);
        if (isSelected) {
            setSelected(selected.filter((item) => item.id !== row.id));
        } else {
            setSelected([...selected, row]);
        }
    }

    const selectAll = () => {
        if (selected.length === auxDataSource?.length) {
            setSelected([]);
        } else {
            setSelected(auxDataSource || []);
        }
    }

    const customCellRender = (column: string, index: number, row: ReplenishmentSpareParts) => {
        if (column === "select") {
            const isSelected = selected.some((item) => item.id === row.id);
            return (
                <Checkbox
                    checked={isSelected}
                    onChange={() => changeSelected(row)}
                    disabled={disabled || processing || !hasPermission}
                />
            );
        }
        if (column === "custom_action") {
            return (
                <IconButton
                    className="hover-icon"
                    onClick={() => {
                        setOpenStock(true);
                        setSelectedRow(row);
                    }}
                    disabled={processing}
                >
                    <Troubleshoot />
                </IconButton>
            );
        }
        if (column === "material_number") {
            return (
                <Box display="flex" flexDirection="column">
                    <Typography variant="body1" color="primary"><b>COD: {row.material_number}</b></Typography>
                    <Typography variant="body1"><b>EN: </b> {row.description_english}</Typography>
                    <Typography variant="body1" color="gray"><b>PT:</b>  {row.description_english}</Typography>
                </Box>
            );
        }
        return "-";
    };

    const customHeadRender = (column: string, index: number) => {
        if (column === "select") {
            const isSelected = selected.length === auxDataSource?.length;
            return (
                <Checkbox
                    checked={isSelected}
                    onChange={() => selectAll()}
                    disabled={disabled || loading || processing}
                />
            );
        }
        if (column === "life_cycle_value") {
            const lifeCycle = configuration?.result === "LIFE_CYCLE" ? "Ciclo de vida" : "Vida útil";
            return lifeCycle;
        }
        if (column === "expected_quantity") {
            const expectedQuantity = `Qtde. Prevista (${configuration?.month_forecast} meses)`
            return expectedQuantity;
        }
        return "-";
    };

    const allSearchSelected = () => {
        return selectedSearch.length === auxDataSource?.length;
    }

    const renderSelection = (selected: any, dataSource: any[], allSelected: () => boolean) => {
        if (allSelected()) {
            return translate.t('all');
        }

        const sorted = selected
            .map((it: number) => dataSource.find(item => item.id === it))
            .filter(Boolean)
            .sort()
            .map((item: any) => {
                if (item && item.material) {
                    return item.material;
                }
                return item;
            });

        return sorted.join(", ");
    };

    const getDescription = (id: number): ReplenishmentSpareParts | undefined => {
        const item = auxDataSource?.find((item) => item.id === id);
        return item;
    }

    const consult = () => {
        const selecteds = selected.map(it => it.material_number);

        inExecution(true);
        service.forecast(selecteds)
            .then((response) => {
                if (response.data) {
                    load();
                }
            })
            .catch((error: any) => {
                ErrorComponent(error);
            });
    }

    const filterListFn = (search: string) => {
        if (!search) {
            setFilterList(auxDataSource?.map((item: any) => item.id.toString()) || []);
            return;
        }

        const filtered = auxDataSource?.filter((item: any) => {
            return item.material_number.toLowerCase().includes(search.toLowerCase()) ||
                item.description_portuguese.toLowerCase().includes(search.toLowerCase());
        });

        setFilterList(filtered?.map((item: any) => item.id.toString()) || []);
    }

    return (
        <Box py={4}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Grid width="40%">
                    <FormControl fullWidth>
                        <InputLabel>
                            Filtrar por materiais
                        </InputLabel>
                        <Select
                            name="group"
                            label={"Filtrar por materiais"}
                            variant={"outlined"}
                            fullWidth
                            aria-placeholder="Filtrar por materiais"
                            autoFocus={false}
                            value={selectedSearch}
                            onChange={(event: any) => {
                                if (event.target.value.includes(999999)) {
                                    return;
                                }

                                if (event.target.value.includes(0)) {
                                    if (!allSearchSelected()) {
                                        setSelectedSearch(filterList.map((p: string) => p));
                                        return;
                                    }
                                    setSelectedSearch([]);
                                    return;
                                }

                                setSelectedSearch(event.target.value)
                            }}
                            multiple
                            renderValue={(selected) => renderSelection(selected, selectedSearch, allSearchSelected)}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: '400px',
                                    },
                                },
                                disableAutoFocusItem: true,
                                disablePortal: true
                            }}
                            disabled={processing}
                        >
                            <MenuItem key={999999} value={999999} disabled={loading}>
                                <ListItemText primary={
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <FormControl fullWidth>
                                            <TextField
                                                sx={{minWidth: "150px !important", marginTop: "10px"}}
                                                size="small"
                                                name="search"
                                                label={""}
                                                fullWidth
                                                variant={"outlined"}
                                                autoFocus={false}
                                                value={search}
                                                placeholder="Pesquisar por Código do Material ou Descrição"
                                                onChange={(event: React.ChangeEvent<{
                                                    value: string
                                                }>) => {
                                                    setSearch(event.target.value);
                                                    filterListFn(event.target.value);
                                                }}
                                                onKeyDown={(event) => {
                                                    event.stopPropagation();
                                                }}
                                            />
                                        </FormControl>
                                    </Box>
                                }/>
                            </MenuItem>
                            <MenuItem key={0} value={0} disabled={loading}>
                                <Checkbox checked={allSearchSelected()}/>
                                <ListItemText primary={
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography variant="body1">{translate.t('all')}</Typography>
                                        {allSearchSelected() &&
                                            <Typography variant="body2"
                                                        color="gray">{translate.t('clear')}
                                            </Typography>
                                        }
                                    </Box>
                                }/>
                            </MenuItem>
                            {filterList?.map((p: any) => (
                                <MenuItem key={p} value={p} disabled={loading}>
                                    <Checkbox checked={selectedSearch.includes(p)}/>
                                    <ListItemText primary={
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Typography variant="body1"><b>{getDescription(parseInt(p))?.material_number || p}</b></Typography>
                                            <Typography>-</Typography>
                                            <Typography variant="body1"> {getDescription(parseInt(p))?.description_portuguese || p}</Typography>
                                        </Box>
                                    }/>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                {hasPermission && (<Button
                    color="secondary"
                    variant="contained"
                    disabled={loading || disabled || processing}
                    onClick={() => {
                        consult();
                    }}>
                    CONSULTAR
                </Button>)}

            </Box>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <TablePaginationComponent
                    state={dataSource}
                    displayedColumnsShow={["select", "Código, Descrição", "Qtde. Produzido", "Consumido", "<Ciclo de vida ou Vida útil>", "Estoque", "PO Aberta", "L/T", "Qtde. Prevista (<X> meses)", "Estoque de segurança", "Qtde. Material p/ Reposição", "Próxima data de compra", ""]}
                    columnStyles={{8:"5%"}}
                    displayedColumns={["select", "material_number", "quantity_produced", "quantity_consumed", "life_cycle_value", "stock",  "open_purchase_order", "lead_time","expected_quantity", "safety_stock", "quantity_material_replacement", "next_purchase_date", "custom_action"]}
                    numberColumns={["life_cycle_value", "quantity_produced", "quantity_consumed", "quantity_material_replacement", "stock",  "open_purchase_order", "lead_time","expected_quantity", "safety_stock"]}
                    integerColumns={["quantity_produced", "quantity_consumed", "quantity_material_replacement", "stock",  "open_purchase_order", "expected_quantity", "safety_stock", "lead_time"]}
                    dateColumns={["next_purchase_date"]}
                    actionsComponent={TableActionComponent}
                    loading={loading}
                    customColumns={["select", "material_number", "custom_action"]}
                    customCellRender={customCellRender}
                    highlightedColumns={["safety_stock", "quantity_material_replacement", "next_purchase_date"]}
                    highlightedColumnsColor={{ safety_stock: "#edeffd", quantity_material_replacement: "#fdebec", next_purchase_date: "#fdebec" }}
                    customHead={["select", "life_cycle_value", "expected_quantity"]}
                    customHeadRender={customHeadRender}
                />
            </Paper>

            <ReplenishmentStockComponent
                openDialog={openStock}
                selectedRow={selectedRow!}
                handleCloseDialog={() => {
                    setOpenStock(false);
                }}/>
        </Box>
    );
}

export default ReplenishmentListComponent;
